import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import { Raumstandhinweis } from './Raumstandhinweis';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Punktverfahren je Raum (Paket H4) — Ergänzung des Beleuchtungsnachweises
 * (V-E4, Seite „Beleuchtung") um Ē, Emin und die Gleichmäßigkeit U0, die das
 * Wirkungsgradverfahren dort ausdrücklich nicht liefert.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * KEINE LICHTSTÄRKEVERTEILUNG — DER VORBEHALT STEHT OBEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Für keine Leuchte dieses Bauwerks liegt eine gemessene
 * Lichtstärkeverteilungskurve (LDT) vor. Gerechnet wird mit einer benannten
 * Lambert-Näherung aus dem Lichtstrom — jede Zahl auf dieser Seite ist eine
 * RECHNERISCHE NÄHERUNG und keine Messung.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * AUFBAU: LISTE LINKS, GRUNDRISS DES GEWÄHLTEN RAUMES RECHTS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kein eigener Baumknoten je Raum — bei über 900 Räumen wäre das ein Baum,
 * den niemand mehr überblickt. Die Auswahl bleibt eine Zeile in dieser Seite,
 * wie beim Beleuchtungsnachweis selbst.
 */
export function ElektroBeleuchtungPunktverfahren() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [befund, setBefund] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  const [svgUrl, setSvgUrl] = useState<string | null>(null);
  const [svgFehler, setSvgFehler] = useState<string | null>(null);

  const laden = useCallback(() => {
    if (!projekt) return;
    setFehler(null);

    api.elektroBeleuchtungPunktverfahren(projekt.id, { befund: befund || undefined })
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id, befund]);

  useEffect(() => { laden(); }, [laden]);

  // Gemerkt aus demselben Grund wie auf der Seite „Beleuchtung": Ein bei
  // jedem Durchlauf neu erzeugtes Feld treibt jeden Effekt an, der daran
  // hängt (Block U, Paket U6b).
  const zeilen: any[] = useMemo(() => d?.zeilen ?? [], [d]);
  const lauf = d?.lauf ?? null;
  const hinweise: string[] = useMemo(() => d?.hinweise ?? [], [d]);

  const raum = useMemo(
    () => zeilen.find((z) => z.id === gewaehlt) ?? null,
    [zeilen, gewaehlt]);

  useEffect(() => {
    if (svgUrl) { URL.revokeObjectURL(svgUrl); setSvgUrl(null); }
    setSvgFehler(null);

    if (!projekt || !raum?.roomId || !lauf) return;

    api.elektroBeleuchtungPunktverfahrenRaumSvg(projekt.id, raum.roomId, lauf.id)
      .then(setSvgUrl)
      .catch((e) => setSvgFehler(e instanceof Error ? e.message : String(e)));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projekt?.id, raum?.roomId, lauf?.id]);

  const zahl = (w: number | null | undefined, n = 0) =>
    w === null || w === undefined
      ? '–'
      : w.toLocaleString('de-DE',
          { minimumFractionDigits: n, maximumFractionDigits: n });

  const befundZustand = (b: string): Zustand =>
    (b === 'erfuellt' ? 'gut'
      : b === 'unterversorgt' ? 'fehler'
        : b === 'ueberversorgt' ? 'warnung'
          : 'neutral');

  const befundtext = (b: string) => ({
    erfuellt: 'erfüllt',
    unterversorgt: 'unterversorgt',
    ueberversorgt: 'überversorgt',
    ohne_polygon: 'ohne Raumpolygon',
    ohne_position: 'ohne verortete Leuchte',
    ohne_wirkungsgradlauf: 'ohne Wirkungsgradlauf',
  } as Record<string, string>)[b] ?? b;

  const spalten: Spalte<any>[] = useMemo(() => [
    {
      schluessel: 'raum', titel: 'Raum', immerSichtbar: true,
      wert: (z) => `${z.raumnummer ?? ''} ${z.raumname ?? ''}`.trim(),
    },
    {
      schluessel: 'soll', titel: 'Soll', art: 'zahl', einheit: 'lx',
      wert: (z) => z.wartungswertSollLx, breite: '90px',
    },
    {
      schluessel: 'emittel', titel: 'Ē', art: 'zahl', einheit: 'lx', nachkomma: 1,
      titelHinweis: 'mittlere Beleuchtungsstärke der Rasterpunkte',
      wert: (z) => z.eMittelLx, breite: '90px',
    },
    {
      schluessel: 'emin', titel: 'Emin', art: 'zahl', einheit: 'lx', nachkomma: 1,
      wert: (z) => z.eMinLx, breite: '90px',
    },
    {
      schluessel: 'u0', titel: 'U0', art: 'zahl', nachkomma: 2,
      titelHinweis: 'Gleichmäßigkeit Emin/Ē', wert: (z) => z.gleichmaessigkeitU0Ist,
      breite: '80px',
    },
    {
      schluessel: 'befund', titel: 'Befund', art: 'status', breite: '180px',
      wert: (z) => befundtext(z.befund),
      inhalt: (z) => (
        <StatusBadge zustand={befundZustand(z.befund)} text={befundtext(z.befund)} klein />
      ),
    },
  ], []);

  const kopf = (
    <PageHeader
      titel="Beleuchtung — Punktverfahren (Ē, Emin, U0)"
      beschreibung={'Beleuchtungsstärke an jedem Rasterpunkt aus der tatsächlichen Lage '
        + 'jeder Leuchte, aufbauend auf dem Wirkungsgradlauf des Beleuchtungsnachweises.'}
      meta={lauf ? (
        <>
          Raster {zahl(lauf.rasterM, 2)} m · gerechnet am{' '}
          {new Date(lauf.gerechnetAm).toLocaleString('de-DE')}
          {lauf.gerechnetVon ? ` von ${lauf.gerechnetVon}` : ''}
        </>
      ) : undefined}
      aktionen={(projekt && lauf) ? (
        <button
          className="m-knopf m-knopf-haupt"
          onClick={() => api.elektroBeleuchtungPunktverfahrenPdf(projekt.id)}
        >
          Nachweis ELT-BUP als PDF (je Raum ein Blatt)
        </button>
      ) : undefined}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Das Punktverfahren konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!projekt || !d) {
    return (
      <>
        {kopf}
        <LoadingState text="Punktverfahren wird geladen …" zeilen={8} spalten={6} />
      </>
    );
  }

  if (!lauf) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Für dieses Modell wurde noch kein Punktverfahren gerechnet."
          erklaerung={d.meldung
            ?? 'Das Punktverfahren setzt einen Wirkungsgradlauf des Beleuchtungsnachweises '
            + 'und verortete Leuchten voraus.'}
          aktion={(
            <button className="m-knopf m-knopf-haupt" onClick={laden}>
              Erneut nachsehen
            </button>
          )}
        />
      </>
    );
  }

  return (
    <>
      {kopf}
      <Raumstandhinweis api={api} projektId={projekt.id} rechnungen={['beleuchtung-punkt']} />

      <div className="hinweis warnung" style={{ display: 'block', maxWidth: 860 }}>
        <strong>Lambert-Näherung, keine Herstellerkurve.</strong> Für keine
        Leuchte dieses Bauwerks liegt eine gemessene
        Lichtstärkeverteilungskurve (LDT) vor. Angesetzt ist ein
        Lambert-Strahler aus dem Lichtstrom (n = {zahl(lauf.lambertExponent, 1)},
        I0 = Φ · {zahl(lauf.i0JeLumen, 5)} cd/lm) — eine benannte Näherung.
        Jede Ē-, Emin- und U0-Zahl dieser Seite ist eine RECHNERISCHE
        NÄHERUNG und keine Messung.
      </div>

      <KpiReihe>
        <KpiCard titel="Räume gesamt" wert={lauf.raeumeGesamt} />
        <KpiCard
          titel="gerechnet" wert={lauf.raeumeGerechnet}
          vergleich={`von ${zahl(lauf.raeumeGesamt)} Räumen`}
        />
        <KpiCard
          titel="ohne Polygon" wert={lauf.raeumeOhnePolygon}
          zustand={lauf.raeumeOhnePolygon > 0 ? 'warnung' : 'gut'}
          zustandText={lauf.raeumeOhnePolygon > 0 ? 'nicht rechenbar' : 'keiner'}
        />
        <KpiCard
          titel="ohne Position" wert={lauf.raeumeOhnePosition}
          zustand={lauf.raeumeOhnePosition > 0 ? 'warnung' : 'gut'}
          zustandText={lauf.raeumeOhnePosition > 0 ? 'Leuchte nicht verortet' : 'keiner'}
        />
        <KpiCard
          titel="Leuchten verortet" wert={lauf.leuchtenMitPosition}
          vergleich={`von ${zahl(lauf.leuchtenGesamt)} Leuchten`}
        />
      </KpiReihe>

      {hinweise.length > 0 && (
        <ContentSection titel="Was der Lauf zu seinen eigenen Zahlen sagt">
          <ul className="schwach" style={{ maxWidth: 860 }}>
            {hinweise.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </ContentSection>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 480px', minWidth: 340 }}>
          <DataTable
            name="Räume"
            spalten={spalten}
            zeilen={zeilen}
            schluessel={(z) => String(z.id)}
            suchePlatzhalter="Raum"
            ausfuhr
            ausfuhrDateiname="punktverfahren"
            hoehe={560}
            beiZeileKlick={(z) => setGewaehlt(z.id === gewaehlt ? null : z.id)}
            aktiveZeile={gewaehlt ?? undefined}
            werkzeuge={(
              <select
                value={befund}
                onChange={(e) => setBefund(e.target.value)}
                aria-label="Befund"
                style={{ width: 'auto' }}
              >
                <option value="">alle Befunde</option>
                <option value="erfuellt">erfüllt</option>
                <option value="unterversorgt">unterversorgt</option>
                <option value="ueberversorgt">überversorgt</option>
                <option value="ohne_polygon">ohne Polygon</option>
                <option value="ohne_position">ohne Position</option>
                <option value="ohne_wirkungsgradlauf">ohne Wirkungsgradlauf</option>
              </select>
            )}
            leer={(
              <EmptyState
                titel={befund
                  ? 'Kein Raum passt zu diesem Befund.'
                  : 'Der Lauf hat keinen Raum gerechnet.'}
                erklaerung={befund
                  ? 'Der Befundfilter wirkt auf dem Server und schränkt die Menge ein.'
                  : 'Ohne Raumpolygon und verortete Leuchten gibt es keinen Rasterpunkt zu rechnen.'}
                aktion={befund ? (
                  <button className="m-knopf" onClick={() => setBefund('')}>
                    Befundfilter aufheben
                  </button>
                ) : undefined}
              />
            )}
          />
        </div>

        <div style={{ flex: '1 1 420px', minWidth: 340 }}>
          <ContentSection
            titel={raum ? `${raum.raumnummer} ${raum.raumname}` : 'Grundriss und Isolux'}
            beschreibung={raum ? raum.befundtext : undefined}
          >
            {!raum && (
              <EmptyState
                klein
                titel="Noch kein Raum gewählt."
                erklaerung="Eine Zeile links wählen — dann steht hier der Grundriss mit den Isoluxlinien."
              />
            )}

            {raum && svgFehler && (
              <ErrorState
                klein
                meldung="Der Grundriss dieses Raumes konnte nicht erzeugt werden."
                technisch={svgFehler}
              />
            )}

            {raum && !svgFehler && !svgUrl && (
              <LoadingState text="Grundriss wird erzeugt …" art="spinner" />
            )}

            {raum && !svgFehler && svgUrl && (
              <img
                src={svgUrl}
                alt={`Grundriss ${raum.raumnummer}`}
                style={{ width: '100%', border: '1px solid var(--rand, #ddd)' }}
              />
            )}
          </ContentSection>
        </div>
      </div>
    </>
  );
}
